/**
 * Graph — Mutable graph model.
 *
 * Wraps the serializable graph data with add/remove/connect operations,
 * undo-friendly snapshots, and adjacency queries.
 *
 * Generic over node and variable types for domain-specific extensions.
 */

import type {
  BaseGraphData,
  BaseGraphVariable,
  BaseNodeInstance,
  Edge,
  GraphMetadata,
  PortRef,
  Vec2,
} from './types';

// ============================================================================
// ID generation
// ============================================================================

let _counter = 0;

/**
 * Generate a unique ID.
 * Uses timestamp + counter for uniqueness.
 */
export function uid(prefix = 'g'): string {
  return `${prefix}_${Date.now().toString(36)}_${(++_counter).toString(36)}`;
}

/**
 * Reset the ID counter (useful for testing).
 */
export function resetUidCounter(): void {
  _counter = 0;
}

// ============================================================================
// Graph class
// ============================================================================

/**
 * Mutable graph model with operations for adding/removing nodes and edges.
 *
 * @typeParam TNode - Node instance type (must extend BaseNodeInstance)
 * @typeParam TVariable - Variable type (must extend BaseGraphVariable)
 */
export class Graph<
  TNode extends BaseNodeInstance = BaseNodeInstance,
  TVariable extends BaseGraphVariable = BaseGraphVariable,
> {
  private _id: string;
  private _name: string;
  private _nodes: Map<string, TNode>;
  private _edges: Map<string, Edge>;
  private _variables: Map<string, TVariable>;
  private _metadata?: GraphMetadata;

  /** Adjacency: nodeId → Set of edge IDs touching that node */
  private _nodeEdges = new Map<string, Set<string>>();

  /** Port → edge lookup: "nodeId:portId" → edge ID (for single-connect ports) */
  private _inputEdge = new Map<string, string>();

  /** Output → multiple edges: "nodeId:portId" → Set<edgeId> */
  private _outputEdges = new Map<string, Set<string>>();

  // --------------------------------------------------------------------------
  // Construction
  // --------------------------------------------------------------------------

  constructor(data?: BaseGraphData<TNode, TVariable>) {
    this._id = data?.id ?? uid('graph');
    this._name = data?.name ?? 'Untitled';
    this._nodes = new Map();
    this._edges = new Map();
    this._variables = new Map();
    this._metadata = data?.metadata;

    if (data) {
      for (const n of data.nodes) {
        this._nodes.set(n.id, { ...n, inputValues: { ...n.inputValues } } as TNode);
      }
      for (const v of data.variables) {
        this._variables.set(v.id, v);
      }
      for (const e of data.edges) {
        this._addEdgeInternal(e);
      }
    }
  }

  // --------------------------------------------------------------------------
  // Properties
  // --------------------------------------------------------------------------

  get id(): string { return this._id; }
  get name(): string { return this._name; }
  set name(v: string) { this._name = v; }
  get metadata(): GraphMetadata | undefined { return this._metadata; }
  set metadata(v: GraphMetadata | undefined) { this._metadata = v; }
  get nodeCount(): number { return this._nodes.size; }
  get edgeCount(): number { return this._edges.size; }

  // --------------------------------------------------------------------------
  // Nodes
  // --------------------------------------------------------------------------

  /**
   * Add a node instance. Returns the new node's ID.
   */
  addNode(
    type: string,
    position: Vec2 = { x: 0, y: 0 },
    inputValues: Record<string, unknown> = {},
    extra?: Partial<Omit<TNode, 'id' | 'type' | 'position' | 'inputValues'>>,
  ): string {
    const id = uid('n');
    const node = {
      id,
      type,
      position: { ...position },
      inputValues: { ...inputValues },
      ...extra,
    } as TNode;
    this._nodes.set(id, node);
    return id;
  }

  /**
   * Add a node with a specific ID (for deserialization).
   */
  addNodeWithId(node: TNode): void {
    this._nodes.set(node.id, { ...node, inputValues: { ...node.inputValues } } as TNode);
  }

  /**
   * Remove a node and all its edges.
   */
  removeNode(nodeId: string): boolean {
    if (!this._nodes.has(nodeId)) return false;

    // Remove all edges touching this node
    const edgeIds = this._nodeEdges.get(nodeId);
    if (edgeIds) {
      for (const eid of [...edgeIds]) {
        this.removeEdge(eid);
      }
    }
    this._nodeEdges.delete(nodeId);
    this._nodes.delete(nodeId);
    return true;
  }

  /**
   * Get a node by ID.
   */
  getNode(nodeId: string): TNode | undefined {
    return this._nodes.get(nodeId);
  }

  /**
   * Get all node instances.
   */
  getNodes(): readonly TNode[] {
    return [...this._nodes.values()];
  }

  /**
   * Check if a node exists.
   */
  hasNode(nodeId: string): boolean {
    return this._nodes.has(nodeId);
  }

  /**
   * Move a node to a new position.
   */
  moveNode(nodeId: string, position: Vec2): void {
    const node = this._nodes.get(nodeId);
    if (node) node.position = { ...position };
  }

  /**
   * Set an inline input value on a node.
   */
  setInputValue(nodeId: string, portId: string, value: unknown): void {
    const node = this._nodes.get(nodeId);
    if (node) (node.inputValues as Record<string, unknown>)[portId] = value;
  }

  /**
   * Get an inline input value from a node.
   */
  getInputValue(nodeId: string, portId: string): unknown {
    const node = this._nodes.get(nodeId);
    return node?.inputValues[portId];
  }

  /**
   * Set dynamic config on a node.
   */
  setNodeConfig(nodeId: string, config: Record<string, unknown>): void {
    const node = this._nodes.get(nodeId);
    if (node) (node as BaseNodeInstance).config = config;
  }

  /**
   * Set comment on a node.
   */
  setNodeComment(nodeId: string, comment: string | undefined): void {
    const node = this._nodes.get(nodeId);
    if (node) (node as BaseNodeInstance).comment = comment;
  }

  // --------------------------------------------------------------------------
  // Edges
  // --------------------------------------------------------------------------

  /**
   * Connect two ports. Returns edge ID or null if invalid.
   *
   * Rules:
   * - Nodes must exist
   * - No self-connections
   * - One edge per input port (replaces existing)
   */
  connect(from: PortRef, to: PortRef): string | null {
    // Nodes must exist
    if (!this._nodes.has(from.nodeId) || !this._nodes.has(to.nodeId)) return null;

    // No self-connections
    if (from.nodeId === to.nodeId) return null;

    // Remove existing edge on the target input (single connection per input)
    const inputKey = `${to.nodeId}:${to.portId}`;
    const existingInputEdge = this._inputEdge.get(inputKey);
    if (existingInputEdge) {
      this.removeEdge(existingInputEdge);
    }

    const id = uid('e');
    const edge: Edge = { id, from: { ...from }, to: { ...to } };
    this._addEdgeInternal(edge);
    return id;
  }

  /**
   * Add an edge with a specific ID (for deserialization).
   */
  addEdgeWithId(edge: Edge): boolean {
    if (!this._nodes.has(edge.from.nodeId) || !this._nodes.has(edge.to.nodeId)) return false;
    if (edge.from.nodeId === edge.to.nodeId) return false;

    // Remove existing edge on target input
    const inputKey = `${edge.to.nodeId}:${edge.to.portId}`;
    const existingInputEdge = this._inputEdge.get(inputKey);
    if (existingInputEdge) {
      this.removeEdge(existingInputEdge);
    }

    this._addEdgeInternal(edge);
    return true;
  }

  /**
   * Remove an edge.
   */
  removeEdge(edgeId: string): boolean {
    const edge = this._edges.get(edgeId);
    if (!edge) return false;

    this._edges.delete(edgeId);

    // Update adjacency
    this._nodeEdges.get(edge.from.nodeId)?.delete(edgeId);
    this._nodeEdges.get(edge.to.nodeId)?.delete(edgeId);

    // Update port indices
    const inputKey = `${edge.to.nodeId}:${edge.to.portId}`;
    if (this._inputEdge.get(inputKey) === edgeId) {
      this._inputEdge.delete(inputKey);
    }

    const outputKey = `${edge.from.nodeId}:${edge.from.portId}`;
    this._outputEdges.get(outputKey)?.delete(edgeId);

    return true;
  }

  /**
   * Get an edge by ID.
   */
  getEdge(edgeId: string): Edge | undefined {
    return this._edges.get(edgeId);
  }

  /**
   * Get all edges.
   */
  getEdges(): readonly Edge[] {
    return [...this._edges.values()];
  }

  /**
   * Check if an edge exists.
   */
  hasEdge(edgeId: string): boolean {
    return this._edges.has(edgeId);
  }

  /**
   * Get all edges connected to a node.
   */
  getNodeEdges(nodeId: string): readonly Edge[] {
    const ids = this._nodeEdges.get(nodeId);
    if (!ids) return [];
    return [...ids].map(id => this._edges.get(id)!).filter(Boolean);
  }

  /**
   * Get the edge connected to a specific input port (if any).
   */
  getInputEdge(nodeId: string, portId: string): Edge | undefined {
    const key = `${nodeId}:${portId}`;
    const edgeId = this._inputEdge.get(key);
    return edgeId ? this._edges.get(edgeId) : undefined;
  }

  /**
   * Check if an input port is connected.
   */
  isInputConnected(nodeId: string, portId: string): boolean {
    const key = `${nodeId}:${portId}`;
    return this._inputEdge.has(key);
  }

  /**
   * Get all edges leaving a specific output port.
   */
  getOutputEdges(nodeId: string, portId: string): readonly Edge[] {
    const key = `${nodeId}:${portId}`;
    const ids = this._outputEdges.get(key);
    if (!ids) return [];
    return [...ids].map(id => this._edges.get(id)!).filter(Boolean);
  }

  /**
   * Check if an output port has any connections.
   */
  isOutputConnected(nodeId: string, portId: string): boolean {
    const key = `${nodeId}:${portId}`;
    const ids = this._outputEdges.get(key);
    return ids !== undefined && ids.size > 0;
  }

  /**
   * Get a map of input port keys to edges (for efficient lookups).
   */
  getInputEdgeMap(): Map<string, Edge> {
    const map = new Map<string, Edge>();
    for (const [key, edgeId] of this._inputEdge) {
      const edge = this._edges.get(edgeId);
      if (edge) map.set(key, edge);
    }
    return map;
  }

  // --------------------------------------------------------------------------
  // Variables
  // --------------------------------------------------------------------------

  /**
   * Add a graph variable. Returns its ID.
   */
  addVariable(
    name: string,
    type: string,
    defaultValue: unknown,
    exposed = false,
    extra?: Partial<Omit<TVariable, 'id' | 'name' | 'type' | 'defaultValue' | 'exposed'>>,
  ): string {
    const id = uid('v');
    const variable = {
      id,
      name,
      type,
      defaultValue,
      exposed,
      ...extra,
    } as TVariable;
    this._variables.set(id, variable);
    return id;
  }

  /**
   * Add a variable with a specific ID (for deserialization).
   */
  addVariableWithId(variable: TVariable): void {
    this._variables.set(variable.id, variable);
  }

  /**
   * Remove a variable.
   */
  removeVariable(varId: string): boolean {
    return this._variables.delete(varId);
  }

  /**
   * Get a variable by ID.
   */
  getVariable(varId: string): TVariable | undefined {
    return this._variables.get(varId);
  }

  /**
   * Find variable by name.
   */
  getVariableByName(name: string): TVariable | undefined {
    for (const v of this._variables.values()) {
      if (v.name === name) return v;
    }
    return undefined;
  }

  /**
   * Get all variables.
   */
  getVariables(): readonly TVariable[] {
    return [...this._variables.values()];
  }

  // --------------------------------------------------------------------------
  // Queries
  // --------------------------------------------------------------------------

  /**
   * Find all nodes of a given type.
   */
  getNodesByType(type: string): readonly TNode[] {
    return [...this._nodes.values()].filter(n => n.type === type);
  }

  /**
   * Get upstream nodes: all nodes that feed data INTO this node.
   */
  getUpstream(nodeId: string): readonly TNode[] {
    const edges = this.getNodeEdges(nodeId);
    const upstream: TNode[] = [];
    for (const e of edges) {
      if (e.to.nodeId === nodeId) {
        const src = this._nodes.get(e.from.nodeId);
        if (src) upstream.push(src);
      }
    }
    return upstream;
  }

  /**
   * Get downstream nodes: all nodes this node feeds data TO.
   */
  getDownstream(nodeId: string): readonly TNode[] {
    const edges = this.getNodeEdges(nodeId);
    const downstream: TNode[] = [];
    for (const e of edges) {
      if (e.from.nodeId === nodeId) {
        const dst = this._nodes.get(e.to.nodeId);
        if (dst) downstream.push(dst);
      }
    }
    return downstream;
  }

  // --------------------------------------------------------------------------
  // Snapshot / serialize
  // --------------------------------------------------------------------------

  /**
   * Produce an immutable snapshot for serialization or undo.
   */
  toData(): BaseGraphData<TNode, TVariable> {
    return {
      id: this._id,
      name: this._name,
      nodes: [...this._nodes.values()].map(n => ({
        ...n,
        position: { ...n.position },
        inputValues: { ...n.inputValues },
        config: n.config ? { ...n.config } : undefined,
      })) as TNode[],
      edges: [...this._edges.values()].map(e => ({
        id: e.id,
        from: { ...e.from },
        to: { ...e.to },
      })),
      variables: [...this._variables.values()],
      metadata: this._metadata ? { ...this._metadata } : undefined,
    };
  }

  /**
   * Create a Graph from serialized data.
   */
  static fromData<
    TNode extends BaseNodeInstance = BaseNodeInstance,
    TVariable extends BaseGraphVariable = BaseGraphVariable,
  >(data: BaseGraphData<TNode, TVariable>): Graph<TNode, TVariable> {
    return new Graph(data);
  }

  /**
   * Clear all nodes, edges, and variables.
   */
  clear(): void {
    this._nodes.clear();
    this._edges.clear();
    this._variables.clear();
    this._nodeEdges.clear();
    this._inputEdge.clear();
    this._outputEdges.clear();
  }

  /**
   * Clone this graph.
   */
  clone(): Graph<TNode, TVariable> {
    return Graph.fromData(this.toData());
  }

  // --------------------------------------------------------------------------
  // Internals
  // --------------------------------------------------------------------------

  private _addEdgeInternal(edge: Edge): void {
    this._edges.set(edge.id, edge);

    // Node adjacency
    for (const nid of [edge.from.nodeId, edge.to.nodeId]) {
      let set = this._nodeEdges.get(nid);
      if (!set) {
        set = new Set();
        this._nodeEdges.set(nid, set);
      }
      set.add(edge.id);
    }

    // Input port → single edge
    const inputKey = `${edge.to.nodeId}:${edge.to.portId}`;
    this._inputEdge.set(inputKey, edge.id);

    // Output port → multiple edges
    const outputKey = `${edge.from.nodeId}:${edge.from.portId}`;
    let outSet = this._outputEdges.get(outputKey);
    if (!outSet) {
      outSet = new Set();
      this._outputEdges.set(outputKey, outSet);
    }
    outSet.add(edge.id);
  }
}
